import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { AtSign, Check, Globe2, ImagePlus, Inbox, Text, Users, X } from 'lucide-react';
import { PROJECT_GRADIENTS, type ProjectCover, type ProjectRuleKind, type ProjectSummary, type WorkspaceMember } from '@mailpilot/shared';
import { useBlobUrl } from '../../hooks/useBlobUrl';
import { relativeTime } from '../../lib/format';
import { Avatar } from '../ui/Avatar';
import { Button, Field, InlineError, Input, Modal } from '../ui';

export function ProjectCoverView({ cover, className, children }: { cover: ProjectCover; className?: string; children?: ReactNode }) {
  const { url: img } = useBlobUrl(cover.kind === 'image' ? cover.value : null);
  const bg = cover.kind === 'gradient' ? PROJECT_GRADIENTS[cover.value] ?? PROJECT_GRADIENTS.ocean : img ? `center / cover no-repeat url("${img}")` : PROJECT_GRADIENTS.slate;
  return (
    <div className={clsx('relative overflow-hidden', className)} style={{ background: bg }}>
      {children}
    </div>
  );
}

/** A project shown as a file folder: a coloured tab, the cover, then the details. */
export function ProjectCard({ p }: { p: ProjectSummary }) {
  const tab = p.cover.kind === 'gradient' ? PROJECT_GRADIENTS[p.cover.value] : PROJECT_GRADIENTS.slate;
  const needs = p.unreadCount;
  return (
    <Link to={`/projects/${p.id}`} className="group block focus:outline-none" aria-label={`${p.name}${needs ? `, ${needs} new` : ''}`}>
      <div className="relative ml-5 flex h-7 w-36 items-center rounded-t-2xl px-3 text-[0.625rem] font-bold uppercase tracking-wider text-white/90 transition-transform group-hover:-translate-y-0.5" style={{ background: tab }} aria-hidden>
        {p.members.length} {p.members.length === 1 ? 'person' : 'people'}
      </div>
      <div className="overflow-hidden rounded-[24px] rounded-tl-lg bg-surface shadow-card ring-1 ring-line/60 transition-[transform,box-shadow] duration-200 group-hover:-translate-y-1 group-hover:shadow-[0_24px_48px_-20px_rgba(15,23,42,0.5)] group-focus-visible:ring-2 group-focus-visible:ring-primary">
        <ProjectCoverView cover={p.cover} className="h-36">
          <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
          {needs > 0 && <span className="mp-pill mp-pill-lime absolute right-3 top-3 shadow">{needs} new</span>}
          <span className="absolute bottom-3 left-4 right-4 truncate text-lg font-semibold text-white drop-shadow">{p.name}</span>
        </ProjectCoverView>
        <div className="space-y-3 p-4">
          <p className="line-clamp-2 min-h-[2.5em] text-[0.8125rem] leading-relaxed text-muted">{p.description || `${p.rules.length} routing rule${p.rules.length === 1 ? '' : 's'} · ${p.conversationCount} conversation${p.conversationCount === 1 ? '' : 's'}`}</p>
          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="flex -space-x-1.5">
              {p.members.slice(0, 5).map((m) => (
                <Avatar key={m.userId} name={m.name} email={m.email} size={26} ring="rgb(var(--surface))" />
              ))}
              {p.members.length > 5 && <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-sunken text-[0.625rem] font-semibold text-muted ring-2 ring-surface">+{p.members.length - 5}</span>}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="mp-pill mp-pill-neutral"><Inbox className="h-3 w-3" aria-hidden /> {p.conversationCount}</span>
              {p.lastActivityAt && <span className="mp-pill mp-pill-lavender">{relativeTime(p.lastActivityAt)}</span>}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

const KIND_ICON: Record<ProjectRuleKind, ReactNode> = { sender: <AtSign className="h-3.5 w-3.5" />, domain: <Globe2 className="h-3.5 w-3.5" />, subject: <Text className="h-3.5 w-3.5" /> };
const KIND_LABEL: Record<ProjectRuleKind, string> = { sender: 'Sender', domain: 'Whole domain', subject: 'Subject contains' };

/** Guess what someone typed: an address → sender, "@acme.com"/"acme.com" → domain, anything else → subject phrase. */
export function guessRule(raw: string): { kind: ProjectRuleKind; value: string } | null {
  const v = raw.trim();
  if (!v) return null;
  const email = v.match(/[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+/);
  if (email && !v.startsWith('@')) return { kind: 'sender', value: email[0].toLowerCase() };
  const d = v.replace(/^@/, '').replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '').toLowerCase();
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d)) return { kind: 'domain', value: d };
  return { kind: 'subject', value: v };
}

/** Senders, domains and subject phrases that route mail into a project. */
export function RulesEditor({ rules, onChange }: { rules: { kind: ProjectRuleKind; value: string }[]; onChange: (r: { kind: ProjectRuleKind; value: string }[]) => void }) {
  const [text, setText] = useState('');
  const add = (e?: FormEvent) => {
    e?.preventDefault();
    const next = text.split(/[,\n]/).map(guessRule).filter((r): r is { kind: ProjectRuleKind; value: string } => !!r);
    if (!next.length) return;
    const key = (r: { kind: string; value: string }) => `${r.kind}:${r.value.toLowerCase()}`;
    const seen = new Set(rules.map(key));
    onChange([...rules, ...next.filter((r) => !seen.has(key(r)))]);
    setText('');
  };
  const cycle = (i: number) => {
    const order: ProjectRuleKind[] = ['sender', 'domain', 'subject'];
    onChange(rules.map((r, j) => (j === i ? { ...r, kind: order[(order.indexOf(r.kind) + 1) % 3]! } : r)));
  };
  return (
    <div className="space-y-2.5">
      <div className="flex gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add(e);
          }}
          placeholder="name@company.com, company.com or a subject phrase"
          aria-label="Add a sender, domain or subject phrase"
        />
        <Button type="button" onClick={() => add()} disabled={!text.trim()}>
          Add
        </Button>
      </div>
      {rules.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {rules.map((r, i) => (
            <li key={`${r.kind}:${r.value}`} className="inline-flex items-center gap-1.5 rounded-full bg-sunken py-1 pl-2.5 pr-1 text-[0.8125rem] text-ink">
              <button type="button" onClick={() => cycle(i)} title={`${KIND_LABEL[r.kind]} — click to change`} className="inline-flex items-center gap-1 text-muted hover:text-ink">
                {KIND_ICON[r.kind]}
                <span className="text-[0.6875rem] font-semibold uppercase tracking-wide">{KIND_LABEL[r.kind]}</span>
              </button>
              <span className="max-w-[16rem] truncate font-medium">{r.value}</span>
              <button type="button" aria-label={`Remove ${r.value}`} onClick={() => onChange(rules.filter((_, j) => j !== i))} className="rounded-full p-0.5 text-muted hover:bg-raised hover:text-ink">
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted">Mail from these senders, from anyone at these domains, or with these words in the subject goes into the project.</p>
      )}
    </div>
  );
}

/** Pick members (and who manages) from the organization. */
export function MemberPicker({ people, value, onChange, lockedId }: { people: WorkspaceMember[]; value: { userId: string; role: 'manager' | 'member' }[]; onChange: (v: { userId: string; role: 'manager' | 'member' }[]) => void; lockedId?: string }) {
  const sel = new Map(value.map((v) => [v.userId, v.role]));
  return (
    <ul className="max-h-72 divide-y divide-line/60 overflow-y-auto rounded-xl ring-1 ring-line/70 scroll-thin">
      {people.map((m) => {
        const role = sel.get(m.userId);
        return (
          <li key={m.userId} className="flex items-center gap-3 px-3 py-2">
            <button
              type="button"
              disabled={m.userId === lockedId}
              aria-pressed={!!role}
              onClick={() => onChange(role ? value.filter((v) => v.userId !== m.userId) : [...value, { userId: m.userId, role: 'member' }])}
              className={clsx('flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors', role ? 'border-primary bg-primary text-primary-fg' : 'border-line bg-surface')}
            >
              {role && <Check className="h-3.5 w-3.5" />}
            </button>
            <Avatar name={m.displayName} email={m.email} size={28} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[0.8125rem] font-medium text-ink">{m.displayName}</span>
              <span className="block truncate text-xs text-muted">{m.email}</span>
            </span>
            {role && (
              <select
                aria-label={`Role for ${m.displayName}`}
                value={role}
                disabled={m.userId === lockedId}
                onChange={(e) => onChange(value.map((v) => (v.userId === m.userId ? { ...v, role: e.target.value as 'manager' | 'member' } : v)))}
                className="rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink"
              >
                <option value="member">Member</option>
                <option value="manager">Manager</option>
              </select>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Cover picker: preset gradients, plus an image upload (once the project exists). */
export function CoverPicker({ value, onChange, onUpload }: { value: ProjectCover; onChange: (c: ProjectCover) => void; onUpload?: (f: File) => void }) {
  const file = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {Object.entries(PROJECT_GRADIENTS).map(([id, g]) => (
        <button
          key={id}
          type="button"
          aria-label={`Cover ${id}`}
          aria-pressed={value.kind === 'gradient' && value.value === id}
          onClick={() => onChange({ kind: 'gradient', value: id })}
          className={clsx('h-10 w-14 rounded-lg ring-offset-2 ring-offset-surface transition-transform hover:scale-105', value.kind === 'gradient' && value.value === id && 'ring-2 ring-primary')}
          style={{ background: g }}
        />
      ))}
      {onUpload && (
        <>
          <button type="button" onClick={() => file.current?.click()} className={clsx('flex h-10 items-center gap-1.5 rounded-lg border border-dashed border-line px-3 text-xs font-medium text-muted hover:border-primary hover:text-primary-ink', value.kind === 'image' && 'border-primary text-primary-ink')}>
            <ImagePlus className="h-4 w-4" /> {value.kind === 'image' ? 'Change image' : 'Upload image'}
          </button>
          <input ref={file} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} />
        </>
      )}
    </div>
  );
}

/** Create a project: name, cover, who it's for, and which mail goes in. */
export function NewProjectModal({ open, onClose, people, meId, onCreate, busy, error }: { open: boolean; onClose: () => void; people: WorkspaceMember[]; meId: string; onCreate: (b: { name: string; description: string; cover: ProjectCover; rules: { kind: ProjectRuleKind; value: string }[]; memberIds: string[] }) => void; busy: boolean; error: unknown }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [cover, setCover] = useState<ProjectCover>({ kind: 'gradient', value: 'ocean' });
  const [rules, setRules] = useState<{ kind: ProjectRuleKind; value: string }[]>([]);
  const [members, setMembers] = useState<{ userId: string; role: 'manager' | 'member' }[]>([{ userId: meId, role: 'manager' }]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="New project"
      description="Group a client, deal or job's emails in one place and choose who follows up."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!name.trim()} onClick={() => onCreate({ name: name.trim(), description: description.trim(), cover, rules, memberIds: members.map((m) => m.userId) })}>
            Create project
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <ProjectCoverView cover={cover} className="flex h-24 items-end rounded-2xl p-4">
          <span className="text-lg font-semibold text-white drop-shadow">{name || 'Project name'}</span>
        </ProjectCoverView>
        <Field label="Name">{(id) => <Input id={id} data-autofocus value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Northwind renovation" />}</Field>
        <Field label="Description (optional)">{(id) => <Input id={id} value={description} maxLength={1000} onChange={(e) => setDescription(e.target.value)} placeholder="What this project is about" />}</Field>
        <Field label="Cover">{() => <CoverPicker value={cover} onChange={setCover} />}</Field>
        <Field label="Which mail goes in" hint="You can change this any time. Existing matching conversations are added straight away.">
          {() => <RulesEditor rules={rules} onChange={setRules} />}
        </Field>
        <Field label={<span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> Who follows up</span>} hint="Members see every conversation in the project. Managers can also change it.">
          {() => <MemberPicker people={people} value={members} onChange={setMembers} lockedId={meId} />}
        </Field>
        <InlineError error={error} />
      </div>
    </Modal>
  );
}
